'use client';

import { useEffect, useState, FormEvent } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Section, SectionHeader, Card } from '@/components/layout';

import { useSiteLanguage } from '@/components/common/SiteLanguageProvider';

interface CareersApplyFormData {
  name: string;
  description: string;
}

interface CareersApplyErrors {
  name?: string;
  description?: string;
  resume?: string;
}

export default function CareersApplySection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const { language } = useSiteLanguage();

  const [formData, setFormData] = useState<CareersApplyFormData>({
    name: '',
    description: '',
  });
  const [errors, setErrors] = useState<CareersApplyErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');

  const copy = {
    en: {
      title: 'Send us your Resume',
      subtitle:
        'Send your resume and a short note. We review applications and reach out if there is a fit.',
      labels: {
        name: 'Your name *',
        description: 'A short description *',
        resume: 'Attach your resume (optional file type)',
      },
      placeholders: {
        name: 'e.g., Jane Doe',
        description:
          'Tell us a little bit about your experience and we will reach out if there is a fit.',
      },
      attachment: {
        chooseFile: 'Choose a file to attach',
        removeFile: 'Remove file',
        maxFile: 'Max 10MB',
      },
      actions: {
        send: 'Send application',
        sending: 'Sending...',
      },
      messages: {
        success: 'Thank you! Your application has been received.',
      },
      errors: {
        nameRequired: 'Name is required',
        descriptionRequired: 'Please add a short description',
        descriptionMin: 'Please provide more detail (at least 10 characters)',
        resumeRequired: 'Please attach your resume',
        attachmentSize: 'File size must be less than 10MB',
        invalidResponse: 'Server returned an invalid response. Please try again later.',
        generic: 'Something went wrong while sending your application. Please try again later.',
        failedToSend: 'Failed to send application. Please try again.',
      },
    },
    fr: {
      title: 'Candidater',
      subtitle:
        'Envoyez votre CV et une courte note. Nous examinons les candidatures et vous contactons en cas de correspondance.',
      labels: {
        name: 'Votre nom *',
        description: 'Une courte description *',
        resume: 'Joindre votre CV',
      },
      placeholders: {
        name: 'ex. Jane Doe',
        description: 'Dites-nous quel poste vous intéresse et un peu de votre expérience.',
      },
      attachment: {
        chooseFile: 'Choisir un fichier à joindre',
        removeFile: 'Retirer le fichier',
        maxFile: '10 Mo max',
      },
      actions: {
        send: 'Envoyer la candidature',
        sending: 'Envoi...',
      },
      messages: {
        success: 'Merci ! Votre candidature a bien été reçue.',
      },
      errors: {
        nameRequired: 'Le nom est requis',
        descriptionRequired: 'Veuillez ajouter une courte description',
        descriptionMin: 'Veuillez fournir plus de détails (au moins 10 caractères)',
        resumeRequired: 'Veuillez joindre votre CV',
        attachmentSize: 'La taille du fichier doit être inférieure à 10 Mo',
        invalidResponse:
          'Le serveur a retourné une réponse invalide. Veuillez réessayer plus tard.',
        generic: "Une erreur est survenue lors de l'envoi. Veuillez réessayer plus tard.",
        failedToSend: "Impossible d'envoyer. Veuillez réessayer.",
      },
    },
  } as const;

  const t = copy[language];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validate = (): boolean => {
    const newErrors: CareersApplyErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t.errors.nameRequired;
    }

    if (!formData.description.trim()) {
      newErrors.description = t.errors.descriptionRequired;
    } else if (formData.description.trim().length < 10) {
      newErrors.description = t.errors.descriptionMin;
    }

    if (!selectedFile) {
      newErrors.resume = t.errors.resumeRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHydrated) return;
    const file = e.target.files?.[0] || null;

    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors((prev) => ({ ...prev, resume: t.errors.attachmentSize }));
        setSelectedFile(null);
        setFileName('');
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      if (errors.resume) {
        setErrors((prev) => ({ ...prev, resume: undefined }));
      }
    } else {
      setSelectedFile(null);
      setFileName('');
    }
  };

  const handleChange = (field: keyof CareersApplyFormData, value: string) => {
    if (!isHydrated) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isHydrated || !validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      if (selectedFile) {
        formDataToSend.append('resume', selectedFile);
      }

      const response = await fetch('/api/careers/apply', {
        method: 'POST',
        body: formDataToSend,
      });

      const contentType = response.headers.get('content-type');
      let data: { error?: string } | undefined;

      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          throw new Error(t.errors.invalidResponse);
        }
      } else {
        throw new Error(t.errors.generic);
      }

      if (!response.ok) {
        const errorMessage = data?.error || t.errors.failedToSend;
        throw new Error(errorMessage);
      }

      setIsSubmitting(false);
      setSubmitSuccess(true);

      setFormData({ name: '', description: '' });
      setSelectedFile(null);
      setFileName('');

      const fileInput = document.getElementById('resume') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';

      setTimeout(() => setSubmitSuccess(false), 6000);
    } catch (error: unknown) {
      setIsSubmitting(false);
      const message = error instanceof Error ? error.message : t.errors.generic;
      setSubmitError(message);
      setTimeout(() => setSubmitError(null), 8000);
    }
  };

  return (
    <Section id="careers-apply" background="background" padding="xl">
      <div className="text-center max-w-3xl mx-auto">
        <SectionHeader title={t.title} description={t.subtitle} />
      </div>

      <div className="max-w-2xl mx-auto mt-6">
        <Card className="p-6 sm:p-8">
          {submitSuccess ? (
            <div className="flex flex-col items-center text-center">
              <div className="p-4 bg-success/10 rounded-lg mb-4">
                <Icon name="CheckCircleIcon" size={32} className="text-success" />
              </div>
              <p className="font-sans text-card-title text-foreground mb-2">{t.messages.success}</p>
              <p className="text-sm text-muted-foreground">
                You can close this page or send another application.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label
                  htmlFor="candidate-name"
                  className="block font-sans text-caption font-medium text-foreground mb-2"
                >
                  {t.labels.name}
                </label>
                <input
                  type="text"
                  id="candidate-name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`w-full px-4 py-3.5 text-base bg-card border ${
                    errors.name ? 'border-error' : 'border-border'
                  } rounded-sm text-foreground placeholder:text-muted-foreground/80 placeholder:opacity-100 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20`}
                  placeholder={t.placeholders.name}
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="resume"
                  className="block font-sans text-caption font-medium text-foreground mb-2"
                >
                  {t.labels.resume}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="resume"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                  />
                  <label
                    htmlFor="resume"
                    className={`flex items-center justify-between w-full px-4 py-4 bg-card border rounded-sm cursor-pointer transition-all duration-200 ${
                      errors.resume
                        ? 'border-error'
                        : 'border-border hover:border-primary focus-within:ring-2 focus-within:ring-primary/20'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon name="PaperClipIcon" size={20} className="text-primary flex-shrink-0" />
                      <span className="text-foreground text-base truncate">
                        {fileName || t.attachment.chooseFile}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground ml-2 flex-shrink-0">
                      {t.attachment.maxFile}
                    </span>
                  </label>
                </div>

                {fileName && (
                  <div className="mt-3 p-3 bg-card border border-border rounded-sm flex items-center gap-3 animate-fade-in">
                    <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                      <Icon name="DocumentTextIcon" size={18} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setFileName('');
                        const fileInput = document.getElementById(
                          'resume'
                        ) as HTMLInputElement | null;
                        if (fileInput) fileInput.value = '';
                      }}
                      className="p-2 text-error hover:text-error/80 hover:bg-error/10 rounded-sm transition-all duration-200 active:scale-95 flex-shrink-0 touch-manipulation"
                      aria-label={t.attachment.removeFile}
                    >
                      <Icon name="XMarkIcon" size={18} />
                    </button>
                  </div>
                )}

                {errors.resume && (
                  <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.resume}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="application-description"
                  className="block font-sans text-caption font-medium text-foreground mb-2"
                >
                  {t.labels.description}
                </label>
                <textarea
                  id="application-description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={5}
                  className={`w-full px-4 py-3.5 text-base bg-card border ${
                    errors.description ? 'border-error' : 'border-border'
                  } rounded-sm text-foreground placeholder:text-muted-foreground/80 placeholder:opacity-100 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none`}
                  placeholder={t.placeholders.description}
                />
                {errors.description && (
                  <p className="mt-2 text-sm text-error flex items-center gap-1.5">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.description}
                  </p>
                )}
              </div>

              {submitError && (
                <div className="bg-error/10 border border-error rounded-sm p-4 flex items-start gap-3">
                  <Icon
                    name="ExclamationCircleIcon"
                    size={24}
                    className="text-error flex-shrink-0 mt-0.5"
                  />
                  <p className="font-sans text-sm font-medium text-error">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-primary text-primary-foreground text-nav font-medium rounded-sm hover:shadow-neon-cyan transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 touch-manipulation"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    <span>{t.actions.sending}</span>
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    <span>{t.actions.send}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </Card>
      </div>
    </Section>
  );
}
